import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DndContext, closestCenter, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ReleaseAwarePointerSensor } from '../utils/dndSensors';
import { useAuth } from '../context/auth';
import api from '../api';
import Search from '../components/Search';
import PromptResponseBanner from '../components/PromptResponseBanner';
import MentionInput from '../components/MentionInput';
import Media from '../types/Media';
import {
  tokensToBracketText,
  mentionsToEditState,
  enrichOrderedMentions,
  resolveMusicMentions,
} from '../mentionUtils';
import { plainNote } from '../utils/listHelpers';
import GridSpot from '../components/GridSpot';
import { usePlayback } from '../context/playback';
import { mediaToQueueItem } from '../utils/queueItems';
import { ReactComponent as PlayIcon } from '../img/play.svg';
import { ReactComponent as PauseIcon } from '../img/pause.svg';
import PlayQueueMenu from '../components/PlayQueueMenu';

// ---------- types ----------

interface ListItemDraft {
  id: string;
  mediaId: string;
  media: Media;
  note: string;
  isEmpty?: boolean;
}

function makeEmptySlot(index: number): ListItemDraft {
  return {
    id: 'empty-' + index + '-' + Math.random().toString(36).slice(2),
    mediaId: '',
    media: {} as Media,
    note: '',
    isEmpty: true,
  };
}

function padToNine(items: ListItemDraft[]): ListItemDraft[] {
  var result = items.slice();
  while (result.length < 9) {
    result.push(makeEmptySlot(result.length));
  }
  return result;
}

function stripEmpty(items: ListItemDraft[]): ListItemDraft[] {
  return items.filter(function (it) {
    return !it.isEmpty;
  });
}

// ---------- styled ----------

const Page = styled.div`
  max-width: 410px;
  margin: 0 auto;
  padding: 1em 0.75em 4em;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25em;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  color: #333;
  padding: 0.3em 0;
`;

const PublishButton = styled.button<{ $ready: boolean }>`
  background: ${(p) => (p.$ready ? 'black' : '#ccc')};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.4em 1em;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: ${(p) => (p.$ready ? 'pointer' : 'not-allowed')};
`;

const SectionLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #666;
  margin-bottom: 0.3em;
  margin-top: 1em;
`;

const TitleInput = styled.input`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.5em 0.75em;
  font-size: 0.9rem;
  font-family: inherit;
  font-weight: 700;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #999;
  }
`;

const ItemsSection = styled.div`
  margin-top: 0.5em;
  min-height: 80px;
`;

const EmptyState = styled.div`
  color: #999;
  font-size: 0.8rem;
  text-align: center;
  padding: 1em 0;
`;

const AddButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: 1px dashed #ccc;
  border-radius: 8px;
  padding: 0.5em;
  font-size: 0.8rem;
  color: #666;
  cursor: pointer;
  margin-top: 0.4em;
  font-family: inherit;

  &:hover {
    border-color: #999;
    color: #333;
  }
`;

const ItemCard = styled.div`
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  background: white;
  margin-bottom: 0.4em;
  overflow: hidden;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.35em 0.4em;
`;

const DragHandle = styled.div`
  cursor: grab;
  color: #ccc;
  display: flex;
  align-items: center;
  padding: 0.2em;
  touch-action: none;
  /* A press on the handle is never the start of a text selection. */
  user-select: none;
  -webkit-user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const ItemThumb = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

// Play/pause for an item, at the right end of its row: an outlined circle,
// black on the white card, as the post's play button is white on black.
const ItemPlayButton = styled.button`
  position: relative;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: 1.5px solid black;
  border-radius: 50%;
  background: white;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 0.7rem;

  /* A tap target past the visible circle. */
  &::after {
    content: '';
    position: absolute;
    inset: -7px;
  }

  &:hover {
    background: #f0f0f0;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ItemArtist = styled.div`
  font-weight: 900;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemTitle = styled.div`
  font-weight: 300;
  font-style: italic;
  font-size: 0.72rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 0.2em;
  display: flex;
  align-items: center;

  &:hover {
    color: #333;
  }
`;

const NoteInput = styled.input`
  width: 100%;
  border: none;
  border-top: 1px solid #f0f0f0;
  padding: 0.4em 0.6em;
  font-size: 0.72rem;
  font-style: italic;
  font-family: inherit;
  box-sizing: border-box;
  background: transparent;

  &:focus {
    outline: none;
    background: #fafafa;
  }
`;

const RankNum = styled.span`
  font-weight: 900;
  font-size: 0.7rem;
  color: #333;
  min-width: 1.4em;
  text-align: center;
  flex-shrink: 0;
`;

const DraftBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f0f0f0;
  border-radius: 8px;
  padding: 0.4em 0.75em;
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 1em;
`;

const DiscardBtn = styled.button`
  background: none;
  border: none;
  color: tomato;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

const ErrorText = styled.div`
  color: tomato;
  font-size: 0.8rem;
  margin-top: 0.5em;
`;

const DupeWarning = styled.div`
  color: tomato;
  font-size: 0.72rem;
  text-align: center;
  padding: 0.3em;
`;

// ---------- success animation ----------

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  0%   { transform: scale(0); }
  60%  { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
  animation: ${fadeIn} 0.4s ease-out;
`;

const Checkmark = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #2ecc71;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${scaleIn} 0.5s ease-out;
  margin-bottom: 1rem;

  &::after {
    content: '';
    display: block;
    width: 20px;
    height: 32px;
    border: solid white;
    border-width: 0 4px 4px 0;
    transform: rotate(45deg);
    margin-top: -4px;
  }
`;

const SuccessText = styled.p`
  font-weight: 700;
  font-size: 1.1rem;
  margin: 0 0 1.5rem 0;
`;

const FeedLink = styled(Link)`
  background: white;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  color: black;
  font-weight: 700;
  font-size: 0.8rem;
  text-decoration: none;
  cursor: pointer;
  animation: ${fadeIn} 0.4s ease-out 0.3s both;
`;

// ---------- sortable item ----------

function SortableItem(props: {
  item: ListItemDraft;
  index: number;
  isRanking: boolean;
  isGrid: boolean;
  totalItems: number;
  // Published lists are frozen: no drag, no remove. Notes stay editable.
  locked: boolean;
  // This item's state in the player, and play/pause for it.
  playState: 'idle' | 'loading' | 'playing' | 'paused';
  onPlay: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNoteChange: (val: string) => void;
  onRemove: () => void;
  onFillSlot: () => void;
  // The empty square the search is filling: outlined, so a tap shows.
  isTarget?: boolean;
}) {
  var { item, index, isRanking, isGrid, totalItems, locked, onNoteChange, onRemove, onFillSlot } =
    props;
  var playState = props.playState;
  var { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.id,
    disabled: locked,
  });

  var style: any = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  // Empty grid slot
  if (item.isEmpty) {
    return (
      <div ref={setNodeRef} style={style}>
        <div
          role="button"
          tabIndex={0}
          aria-label={'Add to spot ' + (index + 1)}
          onClick={onFillSlot}
          onKeyDown={function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onFillSlot();
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4em',
            padding: '0.5em 0',
            cursor: 'pointer',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          {isGrid && <GridSpot index={index} />}
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5em',
              padding: '0.4em 0.6em',
              background: props.isTarget ? 'white' : '#f9f9f9',
              borderRadius: '8px',
              border: props.isTarget ? '1.5px solid black' : '1px dashed #ddd',
              color: props.isTarget ? 'black' : '#bbb',
              fontSize: '0.8rem',
            }}
          >
            + Add
          </div>
        </div>
      </div>
    );
  }

  var media = item.media;
  var songTitle = media.song_title || media.album_title || '';
  var artworkURL = (media.artwork_url || '').replace('{w}x{h}', '80x80');

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
        {isGrid && <GridSpot index={index} />}
        {isRanking && !isGrid && <RankNum>#{totalItems - index}</RankNum>}
        <ItemCard style={{ flex: 1 }}>
          <ItemRow>
            {!locked && (
              <DragHandle {...attributes} {...listeners}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="9" cy="5" r="1.5" />
                  <circle cx="15" cy="5" r="1.5" />
                  <circle cx="9" cy="12" r="1.5" />
                  <circle cx="15" cy="12" r="1.5" />
                  <circle cx="9" cy="19" r="1.5" />
                  <circle cx="15" cy="19" r="1.5" />
                </svg>
              </DragHandle>
            )}
            <ItemThumb src={artworkURL} alt={songTitle} />
            <ItemInfo>
              <ItemArtist>{media.artist}</ItemArtist>
              <ItemTitle>{songTitle}</ItemTitle>
            </ItemInfo>
            {(media.isrc || media.upc) && (
              <ItemPlayButton
                type="button"
                onClick={props.onPlay}
                aria-label={playState === 'playing' ? 'Pause' : 'Play'}
                aria-busy={playState === 'loading'}
              >
                {playState === 'loading' ? (
                  '...'
                ) : playState === 'playing' ? (
                  <PauseIcon width="11" height="11" />
                ) : (
                  <PlayIcon width="11" height="11" style={{ marginLeft: 2 }} />
                )}
              </ItemPlayButton>
            )}
            {!locked && (
              <IconBtn onClick={onRemove} aria-label="Remove">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </IconBtn>
            )}
          </ItemRow>
          {/* 3x3 has no room to show a note over a grid cell, so the grid
              does not offer them at all rather than collecting text that
              would never be displayed. */}
          {/* Notes stay editable after publish: they are each item's
              caption, and a typo is as fixable as one in the list's own. */}
          {!isGrid && (
            <NoteInput
              placeholder="Add a comment..."
              value={item.note}
              maxLength={2000}
              onChange={function (e) {
                onNoteChange(e.target.value);
              }}
            />
          )}
        </ItemCard>
      </div>
    </div>
  );
}

// ---------- main component ----------

interface CreateListProps {
  editPost?: any; // Post object for editing an existing unpublished list
}

// Dropdown for the Kind and Style pickers, matching the feed's Recent/Shuffle
// control rather than inventing a second selector style.
const OptionWrapper = styled.div`
  position: relative;
`;

const OptionButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  text-align: left;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0 1.4rem 0 0.6rem;
  height: 32px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  color: ${(props) => (props.$disabled ? '#999' : '#333')};
  cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};
  position: relative;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    right: 0.45rem;
    top: 50%;
    transform: translateY(-50%);
    border-left: 3.5px solid transparent;
    border-right: 3.5px solid transparent;
    border-top: 4px solid #999;
  }

  &:hover {
    border-color: ${(props) => (props.$disabled ? '#ddd' : '#bbb')};
  }
`;

const OptionMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  min-width: 180px;
  overflow: hidden;
`;

const OptionMenuItem = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  background: ${(props) => (props.$active ? '#f5f5f5' : 'white')};
  border: none;
  padding: 0.5rem 0.75rem;
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
    background: white;
  }
`;

const OptionMenuLabel = styled.div<{ $active?: boolean }>`
  font-size: 0.8rem;
  font-weight: ${(props) => (props.$active ? '700' : '600')};
  color: #333;
`;

const OptionMenuDesc = styled.div`
  font-size: 0.7rem;
  font-weight: 400;
  color: #777;
  line-height: 1.4;
  margin-top: 0.15em;
  white-space: normal;
`;

const PublishHint = styled.div`
  text-align: center;
  font-size: 0.75rem;
  color: #777;
  line-height: 1.4;
  margin: 0.5em 0 0;
`;

const OptionHint = styled.div`
  font-size: 0.7rem;
  color: #777;
  line-height: 1.4;
  margin-top: 0.35em;
`;

const LockedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  margin-bottom: 0.6em;
`;

const UnpublishButton = styled.button`
  flex-shrink: 0;
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.5em 0.75em;
  min-height: 44px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    color: #999;
    border-color: #ccc;
    cursor: default;
  }
`;

interface OptionChoice {
  value: string;
  label: string;
  desc: string;
  // Shown but not choosable; `desc` then says why.
  disabled?: boolean;
}

// Closed it reads as a normal dropdown; open, each choice carries a one-line
// explanation, because "Ranked" and "3x3" are not self-evident.
function OptionSelect(props: {
  options: OptionChoice[];
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  var [open, setOpen] = useState(false);
  var current = props.options.filter(function (o) {
    return o.value === props.value;
  })[0];

  return (
    <OptionWrapper>
      <OptionButton
        type="button"
        $disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={function () {
          if (!props.disabled) setOpen(!open);
        }}
      >
        {current ? current.label : ''}
      </OptionButton>
      {open && !props.disabled && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={function () {
              setOpen(false);
            }}
          />
          <OptionMenu role="listbox">
            {props.options.map(function (o) {
              return (
                <OptionMenuItem
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === props.value}
                  aria-disabled={o.disabled || undefined}
                  disabled={o.disabled}
                  $active={o.value === props.value}
                  onClick={function () {
                    if (o.disabled) return;
                    props.onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <OptionMenuLabel $active={o.value === props.value}>{o.label}</OptionMenuLabel>
                  <OptionMenuDesc>{o.desc}</OptionMenuDesc>
                </OptionMenuItem>
              );
            })}
          </OptionMenu>
        </>
      )}
    </OptionWrapper>
  );
}

var KIND_OPTIONS: OptionChoice[] = [
  { value: 'songs', label: 'Songs', desc: 'Every item is a single track.' },
  { value: 'albums', label: 'Albums', desc: 'Every item is a full album.' },
];

var STYLE_OPTIONS: OptionChoice[] = [
  { value: 'playlist', label: 'Playlist', desc: 'Plays top to bottom, in the order you set.' },
  { value: 'ranking', label: 'Ranked', desc: 'A numbered countdown. Your number one shows last.' },
  { value: 'grid', label: '3x3', desc: 'A nine item grid. Needs exactly nine to publish.' },
];

// Shown only while composing a new list. Once you are editing an existing one
// you already know what a list is, and the space is better spent on the items.
const IntroCard = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin: 0 0 1rem;
`;

const IntroHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
`;

const IntroTitle = styled.div`
  font-weight: 800;
  font-size: 0.95rem;
  margin-bottom: 0.4em;
  flex: 1;
`;

const IntroDismiss = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: #999;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    color: #333;
  }
`;

const IntroBody = styled.div`
  font-size: 0.8rem;
  color: #555;
  line-height: 1.5;
`;

const IntroPoints = styled.ul`
  margin: 0.6em 0 0;
  padding-left: 1.1em;
  font-size: 0.78rem;
  color: #555;
  line-height: 1.5;

  li {
    margin-bottom: 0.25em;
  }
`;

export default function CreateList(props?: CreateListProps) {
  var navigate = useNavigate();
  var authContext = useAuth();
  var authTokens = authContext.authTokens;

  var editPost = props?.editPost;
  var editList = editPost?.list;

  // Hearing an item while building the list. Each play is tagged with its
  // draft item (`sourceKey`, which an album's tracks keep when the player
  // expands it), so its row shows play or pause; a draft has no post, so no
  // play is counted.
  var playback = usePlayback();
  var currentEntry = playback.queue && playback.queue[playback.queueIndex];
  function playStateOf(item: ListItemDraft): 'idle' | 'loading' | 'playing' | 'paused' {
    if (!currentEntry || currentEntry.sourceKey !== 'draft:' + item.id) return 'idle';
    return playback.playbackState as any;
  }
  // Something else already playing (a list queued from the feed, say)
  // asks Play Now or Add to Queue first, as every other play button does;
  // moving between this list's own items does not.
  var _playMenu = useState<{ x: number; y: number; item: ListItemDraft } | null>(null);
  var playMenu = _playMenu[0];
  var setPlayMenu = _playMenu[1];
  // The menu's item, held apart: the menu closes (playMenu goes null)
  // before its choice runs.
  var playMenuItem = playMenu ? playMenu.item : null;
  function draftEntry(item: ListItemDraft) {
    return Object.assign(mediaToQueueItem(null, item.media), { sourceKey: 'draft:' + item.id });
  }
  function playItem(item: ListItemDraft, e: React.MouseEvent<HTMLButtonElement>) {
    if (playStateOf(item) !== 'idle') {
      playback.togglePlayPause();
      return;
    }
    var othersPlaying =
      (playback.playbackState === 'playing' || playback.playbackState === 'paused') &&
      !(currentEntry && String(currentEntry.sourceKey || '').indexOf('draft:') === 0);
    if (othersPlaying) {
      var rect = e.currentTarget.getBoundingClientRect();
      setPlayMenu({ x: rect.left, y: rect.top, item: item });
      return;
    }
    playback.playNow([draftEntry(item)]);
  }
  var editingExisting = !!editPost?.id;

  // Intro card: shown until dismissed, then never again. Persisted server-side
  // through the same dismissed_cards mechanism the feed CTAs use, so it stays
  // dismissed across devices rather than only in this browser. The local flag
  // hides it immediately; localStorage covers the gap before the profile fetch
  // resolves, so it does not flash back on the next visit.
  var INTRO_KEY = 'create_list_intro';
  var [introDismissed, setIntroDismissed] = useState(function () {
    return localStorage.getItem('hr_dismissed_' + INTRO_KEY) === '1';
  });

  useEffect(
    function () {
      if (introDismissed) return;
      var username = authTokens?.user?.username;
      if (!username) return;
      api
        .get('/api/users/profile/' + username)
        .then(function (res: any) {
          var dismissed = (res.data?.dismissedCards || '').split(',').map(function (k: string) {
            return k.trim();
          });
          if (dismissed.indexOf(INTRO_KEY) !== -1) {
            localStorage.setItem('hr_dismissed_' + INTRO_KEY, '1');
            setIntroDismissed(true);
          }
        })
        .catch(function () {});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function dismissIntro() {
    setIntroDismissed(true);
    localStorage.setItem('hr_dismissed_' + INTRO_KEY, '1');
    api.post('/api/users/dismiss-card', { key: INTRO_KEY }).catch(function () {});
  }

  var DRAFT_KEY = 'hr_list_draft';

  // A draft worth restoring has something the person typed or picked. The save
  // effect below used to write one on every visit, empty or not, so "Draft
  // restored" showed every time Create List opened.
  function draftHasContent(d: any) {
    if (!d) return false;
    var picked = (d.items || []).filter(function (it: any) {
      return it && !it.isEmpty;
    });
    return !!((d.title || '').trim() || (d.text || '').trim() || picked.length);
  }

  function loadDraft() {
    // Don't load draft if editing an existing list
    if (editingExisting) return null;
    try {
      var raw = localStorage.getItem(DRAFT_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (draftHasContent(parsed)) return parsed;
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function buildItemsFromEdit(): ListItemDraft[] {
    if (!editList) return [];
    var listItems = editList.items || editList.preview || [];
    var drafts = listItems.map(function (item: any) {
      return {
        id: item.id || 'item-' + Math.random().toString(36).slice(2),
        mediaId: item.media?.id || '',
        media: item.media || {},
        note: plainNote(item.note || ''),
      };
    });
    if (editList.orderStyle !== 'grid') return drafts;
    // A 3x3 comes back with each item at its square (its position), empty
    // squares where the author left them. Anything without a usable square
    // (saved before squares were kept) takes the first free one.
    var squares: ListItemDraft[] = Array.from({ length: 9 }, function (_, i) {
      return makeEmptySlot(i);
    });
    var unplaced: ListItemDraft[] = [];
    listItems.forEach(function (item: any, i: number) {
      var at = item.position;
      if (typeof at === 'number' && at >= 0 && at < 9 && squares[at].isEmpty) {
        squares[at] = drafts[i];
      } else {
        unplaced.push(drafts[i]);
      }
    });
    // Older drafts could hold more than nine (the add-to-list sheet had no
    // cap): the extras stay, as rows past the grid, never silently dropped
    // (the next save would delete them).
    var extras: ListItemDraft[] = [];
    unplaced.forEach(function (draft) {
      var free = squares.findIndex(function (it) {
        return it.isEmpty;
      });
      if (free >= 0) squares[free] = draft;
      else extras.push(draft);
    });
    return squares.concat(extras);
  }

  var saved = useRef(loadDraft());

  // The prompt this list responds to: an existing list's own, or the one a
  // prompt page sent the author here with (/create-list?promptId=...). Kept
  // with the list as a single post keeps it; a draft can drop it, a published
  // list's is locked like its style.
  var [searchParams] = useSearchParams();
  // A restored local draft brings its prompt back; arriving from a prompt
  // page (below) sets that prompt instead.
  var [prompt, setPrompt] = useState<{ id: string; text: string } | null>(
    editPost?.prompt
      ? { id: editPost.prompt.id, text: editPost.prompt.text }
      : saved.current?.prompt || null
  );
  useEffect(function () {
    var promptId = searchParams.get('promptId');
    if (editPost?.id || !promptId) return;
    api
      .get('/api/prompts/' + promptId)
      .then(function (res: any) {
        if (res.data?.id) setPrompt({ id: res.data.id, text: res.data.text });
      })
      .catch(function () {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Media ids of this list's items that have comments or reactions, so
  // removing one can warn first. Only an existing list can have any.
  var [engagedMedia, setEngagedMedia] = useState<Record<string, boolean>>({});
  useEffect(
    function () {
      if (!editPost?.id) return;
      var cancelled = false;
      api
        .get('/api/posts/id/' + editPost.id + '/items')
        .then(function (result) {
          if (cancelled) return;
          var map: Record<string, boolean> = {};
          (result.data || []).forEach(function (p: any) {
            var reactionCount = (p.reactions || []).reduce(function (t: number, r: any) {
              return t + (r.count || 0);
            }, 0);
            if (p.media && p.media.id && ((p.comments || []).length > 0 || reactionCount > 0)) {
              map[p.media.id] = true;
            }
          });
          setEngagedMedia(map);
        })
        .catch(function () {});
      return function () {
        cancelled = true;
      };
    },
    [editPost?.id]
  );

  var [title, setTitle] = useState(editList?.title || saved.current?.title || '');
  var [kind, setKind] = useState<'songs' | 'albums'>(
    editList?.kind || saved.current?.kind || 'songs'
  );
  // The style the + menu or the feed card asked for (/create-list?style=grid).
  // It beats a restored draft's style, as if the author had switched it, but
  // never an existing list's, and never a 3x3 the draft has too many items for.
  var requestedStyle = (function () {
    var v = searchParams.get('style');
    if (editingExisting || (v !== 'playlist' && v !== 'ranking' && v !== 'grid')) return null;
    if (v === 'grid' && stripEmpty(saved.current?.items || []).length > 9) return null;
    return v as 'playlist' | 'ranking' | 'grid';
  })();
  var [orderStyle, setOrderStyle] = useState<'playlist' | 'ranking' | 'grid'>(
    (editList?.orderStyle === 'unordered' ? 'playlist' : editList?.orderStyle) ||
      requestedStyle ||
      saved.current?.orderStyle ||
      'playlist'
  );
  var initialItems = editingExisting ? buildItemsFromEdit() : saved.current?.items || [];
  var [items, setItems] = useState<ListItemDraft[]>(initialItems);
  // The newest items, ahead of React's next render. A pick from search can
  // arrive after an async media create, from a render that is already stale,
  // and two quick picks used to pass the duplicate and cap checks against the
  // same old list. addMediaToList checks and writes through this instead.
  var itemsRef = useRef(items);
  itemsRef.current = items;
  // An existing caption stores music mentions as {mention:N} tokens backed by
  // its musicMentions. MentionInput edits bracket text with chip data instead,
  // the same conversion the feed card's caption editor uses. Sorted by index
  // because the tokens address mentions by placeholderIndex.
  var editMentions = (editPost?.musicMentions || []).slice().sort(function (a: any, b: any) {
    return a.placeholderIndex - b.placeholderIndex;
  });
  var [text, setText] = useState(
    editPost
      ? editMentions.length
        ? tokensToBracketText(editPost.text || '', editMentions)
        : editPost.text || ''
      : saved.current?.text || ''
  );
  var [musicMentions, setMusicMentions] = useState<any[]>(
    editPost ? mentionsToEditState(editMentions) : saved.current?.musicMentions || []
  );
  var [publishing, setPublishing] = useState(false);
  var [published, setPublished] = useState(false);
  var [error, setError] = useState('');
  var [searchOpen, setSearchOpen] = useState(false);
  var [searchKey, setSearchKey] = useState(0);
  var [gridInsertIndex, setGridInsertIndexState] = useState<number | null>(null);
  // The same, ahead of React's next render: a pick resolves after the media
  // is created, and must not read the target from the render it began in.
  var gridInsertIndexRef = useRef<number | null>(null);
  function setGridInsertIndex(index: number | null) {
    gridInsertIndexRef.current = index;
    setGridInsertIndexState(index);
  }
  var [dupeWarning, setDupeWarning] = useState('');
  var [draftRestored, setDraftRestored] = useState(!!saved.current);
  var [savedListId, setSavedListId] = useState<string | null>(editPost?.id || null);

  // Save draft to localStorage on changes. Skipped once the list exists on the
  // server (editing, or created here by a Publish that then failed): from then
  // on the server copy is the draft, and a local one would only bring back a
  // duplicate on the next visit.
  useEffect(
    function () {
      if (editingExisting || savedListId) return;
      var draft = {
        title: title,
        kind: kind,
        orderStyle: orderStyle,
        items: items,
        text: text,
        musicMentions: musicMentions,
        prompt: prompt,
      };
      try {
        if (draftHasContent(draft)) localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        else localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* storage unavailable: nothing to restore later, which is fine */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [title, kind, orderStyle, items, text, musicMentions, prompt, savedListId]
  );

  function clearDraft() {
    localStorage.removeItem(DRAFT_KEY);
  }

  var getSubmitTextRef = useRef<any>(null);

  var sensors = useSensors(
    useSensor(ReleaseAwarePointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  // Counts picked items only: a 3x3 pads itself with empty slots, which used
  // to lock Kind the moment the style was switched, before anything was added.
  var kindLocked = stripEmpty(items).length >= 1;
  var isRanking = orderStyle === 'ranking';
  var isGrid = orderStyle === 'grid';
  // A 3x3 holds nine. With more already picked it is shown but not
  // choosable: switching would leave items off the grid, and trimming them
  // would drop the author's picks without asking.
  var tooManyForGrid = !isGrid && stripEmpty(items).length > 9;
  var styleOptions = STYLE_OPTIONS.map(function (o) {
    return o.value === 'grid' && tooManyForGrid
      ? Object.assign({}, o, {
          disabled: true,
          desc: 'Holds nine. Remove items until there are nine to switch.',
        })
      : o;
  });
  // Picking a style from the + menu while already composing keeps this page
  // mounted, so a new ?style= is applied like a switch in the Style menu.
  // Keyed on the navigation, not the param: picking the same style again
  // (after switching away in the Style menu) is a replace with the same URL.
  var styleParam = searchParams.get('style');
  var locationKey = useLocation().key;
  var firstStyleParam = useRef(true);
  useEffect(
    function () {
      if (firstStyleParam.current) {
        firstStyleParam.current = false;
        return;
      }
      if (
        editingExisting ||
        (styleParam !== 'playlist' && styleParam !== 'ranking' && styleParam !== 'grid')
      )
        return;
      if (styleParam === 'grid' && stripEmpty(itemsRef.current).length > 9) return;
      setGridInsertIndex(null);
      setOrderStyle(styleParam);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationKey]
  );
  var minItems = isGrid ? 9 : 2;
  var maxItems = isGrid ? 9 : 100;
  // When switching to grid mode, pad items to 9
  useEffect(
    function () {
      if (isGrid && items.length < 9) {
        setItems(function (prev) {
          return padToNine(prev);
        });
      }
      if (!isGrid) {
        // Strip empties when leaving grid mode
        setItems(function (prev) {
          return stripEmpty(prev);
        });
      }
    },
    [isGrid]
  );

  var filledItems = stripEmpty(items);

  // Published lists are frozen except for the title, caption and notes. The server
  // enforces this too; disabling the controls just avoids offering an edit that
  // is going to be rejected.
  // State rather than a prop read, because Unpublish flips it in place.
  var [listStatus, setListStatus] = useState(editPost?.status);
  var isPublished = listStatus === 'published';

  // The first unmet requirement, in the order someone would hit them. Shown
  // under the button rather than only on hover, so the reason is discoverable
  // without interacting with a control that does not respond.
  var publishBlocker: string | null = null;
  if (!title.trim()) {
    publishBlocker = 'Add a title to publish.';
  } else if (isGrid && filledItems.length !== 9) {
    publishBlocker = 'A 3x3 grid needs exactly 9 items. You have ' + filledItems.length + '.';
  } else if (filledItems.length < minItems) {
    publishBlocker =
      'Add at least ' + minItems + ' items to publish. You have ' + filledItems.length + '.';
  }

  var canPublish =
    title.trim().length > 0 &&
    filledItems.length >= minItems &&
    (!isGrid || filledItems.length === 9) &&
    !publishing;

  var searchAreaRef = useRef<HTMLDivElement>(null);
  function fillSlot(index: number) {
    setGridInsertIndex(index);
    var wasOpen = searchOpen;
    setSearchOpen(true);
    // Opening mounts the search, which focuses its field. Already open, its
    // field is focused in place, keeping anything typed there: a tap used to
    // look like it did nothing.
    window.requestAnimationFrame(function () {
      var area = searchAreaRef.current;
      if (!area) return;
      if (wasOpen) {
        var field = area.querySelector('input');
        if (field) field.focus({ preventScroll: true });
      }
      if (typeof area.scrollIntoView === 'function') {
        area.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }

  // `pickTarget` is the square chosen when the pick was made; the media may
  // be created on the server first, and the reader may tap another square
  // meanwhile.
  function addMediaToList(media: any, pickTarget: number | null = gridInsertIndexRef.current) {
    // The search panel stays open between picks, so the cap is enforced here,
    // and the panel closes once the list is full rather than offering picks
    // that cannot be added.
    var current = itemsRef.current;
    var filledCount = stripEmpty(current).length;
    if (filledCount >= maxItems) {
      setSearchOpen(false);
      return;
    }
    // Duplicate check
    if (
      current.some(function (it) {
        return it.mediaId === media.id;
      })
    ) {
      setDupeWarning('This item is already in your list');
      setTimeout(function () {
        setDupeWarning('');
      }, 2000);
      return;
    }
    var newItem = {
      id: 'item-' + Date.now() + '-' + Math.random().toString(36).slice(2),
      mediaId: media.id,
      media: media,
      note: '',
    };
    var next: ListItemDraft[];
    if (isGrid) {
      // Fill the tapped slot, or the first empty one when the search stayed
      // open after a previous pick. Never append: a 3x3 has exactly nine
      // slots, and appending used to push picks past the ninth.
      next = padToNine(current);
      var target =
        pickTarget !== null && next[pickTarget]?.isEmpty
          ? pickTarget
          : next.findIndex(function (it) {
              return it.isEmpty;
            });
      if (target < 0) return;
      next[target] = newItem;
    } else if (pickTarget !== null && pickTarget <= current.length) {
      next = current.slice();
      next.splice(pickTarget, 0, newItem);
    } else {
      next = current.concat([newItem]);
    }
    itemsRef.current = next;
    setItems(next);
    // A new square chosen while this pick was on its way keeps its target,
    // and the search keeps what is being typed for it.
    if (gridInsertIndexRef.current === pickTarget) {
      setGridInsertIndex(null);
      setSearchKey(function (k) {
        return k + 1;
      });
    }
    if (filledCount + 1 >= maxItems) setSearchOpen(false);
  }

  function handleAddMedia(media: any) {
    if (!media) return;
    // If media already has a DB id, add directly
    var pickTarget = gridInsertIndexRef.current;
    if (media.id) {
      addMediaToList(media, pickTarget);
      return;
    }
    // Otherwise create the media in the DB first (same as CreatePost)
    api
      .post('/api/media', media)
      .then(function (res: any) {
        addMediaToList(res.data, pickTarget);
      })
      .catch(function (err: any) {
        setError('Failed to add item. Please try again.');
      });
  }

  function handleRemoveItem(itemId: string) {
    // Removing an item deletes its post, and with it everything people said
    // about it. A list unpublished for editing can have items with real
    // conversations, so those ask first.
    var removing = items.find(function (it) {
      return it.id === itemId;
    });
    if (removing && engagedMedia[removing.mediaId]) {
      var noun = kind === 'albums' ? 'album' : 'song';
      if (
        !window.confirm('Remove this ' + noun + '? Its comments and reactions will be deleted too.')
      ) {
        return;
      }
    }
    if (isGrid) {
      // In grid mode, replace with an empty slot to preserve positions; an
      // extra past the ninth square (an older draft held more) goes entirely.
      setItems(function (prev) {
        return prev
          .map(function (it, i) {
            if (it.id !== itemId) return it;
            return i < 9 ? makeEmptySlot(i) : null;
          })
          .filter(Boolean) as ListItemDraft[];
      });
    } else {
      setItems(function (prev) {
        return prev.filter(function (it) {
          return it.id !== itemId;
        });
      });
    }
  }

  function handleNoteChange(itemId: string, val: string) {
    setItems(function (prev) {
      return prev.map(function (it) {
        if (it.id === itemId) return Object.assign({}, it, { note: val });
        return it;
      });
    });
  }

  function handleDragEnd(event: any) {
    var { active, over } = event;
    if (!over || active.id === over.id) return;

    var newItems: ListItemDraft[];

    if (isGrid) {
      var padded = padToNine(items);
      var oldIndex = padded.findIndex(function (it) {
        return it.id === active.id;
      });
      var newIndex = padded.findIndex(function (it) {
        return it.id === over.id;
      });
      if (oldIndex < 0 || newIndex < 0) return;
      newItems = arrayMove(padded, oldIndex, newIndex);
    } else {
      var oldIdx = items.findIndex(function (it) {
        return it.id === active.id;
      });
      var newIdx = items.findIndex(function (it) {
        return it.id === over.id;
      });
      newItems = arrayMove(items, oldIdx, newIdx);
    }

    setItems(newItems);
  }

  var [saving, setSaving] = useState(false);
  var [unpublishing, setUnpublishing] = useState(false);
  var [autosaveError, setAutosaveError] = useState('');

  // Grid lists never display notes, so do not persist any that were typed
  // before the style was switched. Otherwise the stored data claims something
  // the UI will never show.
  // A 3x3 sends each item's square, so the server keeps a draft's empty
  // squares where they are; other styles send their items in order.
  function buildSubmitItems() {
    // A 3x3 holding more than nine (an older draft) has no square for its
    // extras, so it is sent in order, as before squares were kept: every item
    // stays until the author removes some.
    var overfull =
      isGrid &&
      items.slice(9).some(function (it) {
        return !it.isEmpty;
      });
    if (isGrid && !overfull) {
      return items
        .map(function (it, square) {
          return it.isEmpty ? null : { mediaId: it.mediaId, position: square };
        })
        .filter(Boolean);
    }
    return stripEmpty(items).map(function (it) {
      return { mediaId: it.mediaId, note: it.note || undefined };
    });
  }

  // The PATCH body for an existing list. A draft sends its whole item array,
  // notes included, and the server swaps the stored items for it in one
  // transaction. That is the only item write the composer makes: the old
  // per-item add, remove and reorder calls drifted from the screen whenever one
  // failed or raced another, and Publish then published the server's copy
  // rather than what the author was looking at. A published list is frozen
  // except for title, caption and notes: it sends its items unchanged, which
  // the server takes as note edits (a different set or order is refused),
  // and never a style.
  function buildUpdateBase() {
    var update: any = { title: title.trim() || undefined, items: buildSubmitItems() };
    if (!isPublished) {
      update.orderStyle = orderStyle;
      if (prompt) update.promptId = prompt.id;
      else update.clearPrompt = true;
    }
    return update;
  }

  // The caption as it is stored: MentionInput's tokenized text, with a
  // {mention:N} token per music chip, and those mentions in token order. Taken
  // synchronously, at the moment a write is decided, so a queued write sends
  // what the screen showed then.
  function captionSnapshot() {
    var submitData = getSubmitTextRef.current ? getSubmitTextRef.current() : null;
    var captionText = (submitData ? submitData.text : null) || text || '';
    var ordered = submitData
      ? enrichOrderedMentions(submitData.mentions, musicMentions)
      : musicMentions;
    return { text: captionText, ordered: ordered };
  }

  // Songs and albums mentioned from search have no media row until
  // resolveMusicMentions creates one. Ids it hands back are remembered here so
  // the next save, autosave included, does not create the same media again.
  var mentionIdsRef = useRef<Record<string, string>>({});

  function mentionKey(m: any) {
    return m.type + '|' + m.title + '|' + m.artistName;
  }

  function resolveCaption(snap: { text: string; ordered: any[] }) {
    var ordered = snap.ordered.map(function (m: any) {
      var known = !m.mediaId && mentionIdsRef.current[mentionKey(m)];
      return known ? Object.assign({}, m, { mediaId: known }) : m;
    });
    return resolveMusicMentions(ordered).then(function (resolved: any[]) {
      resolved.forEach(function (r: any) {
        if (r.mediaId) mentionIdsRef.current[mentionKey(r)] = r.mediaId;
      });
      // resolveMusicMentions drops a mention whose media could not be
      // created. Saving anyway would store a caption whose {mention:N} tokens
      // point at a missing mention, so fail the save instead: the error shows
      // and the next save tries again.
      if (resolved.length !== ordered.length) {
        throw new Error('A music mention could not be saved');
      }
      return { text: snap.text, mentions: resolved };
    });
  }

  // Every write to an existing list goes through this queue so they reach the
  // server in the order they were made. Two overlapping autosaves could
  // otherwise land out of order and leave the older one stored.
  var saveQueueRef = useRef<Promise<any>>(Promise.resolve());

  // Writes queued or on the wire, so leaving the page can warn while one is
  // still going out.
  var inFlightRef = useRef(0);

  function enqueue(write: () => Promise<any>) {
    inFlightRef.current += 1;
    var run = saveQueueRef.current
      .catch(function () {})
      .then(function () {
        return write();
      });
    run
      .catch(function () {})
      .then(function () {
        inFlightRef.current -= 1;
      });
    saveQueueRef.current = run;
    return run;
  }

  // Queues a PATCH of the list as it is right now. The caption's mentions are
  // resolved inside the queue, so writes still reach the server in order, and
  // the skip check compares against the last write actually sent. With
  // skipIfUnchanged, a body identical to that one is not sent again.
  function queueSave(skipIfUnchanged: boolean) {
    var listId = savedListId;
    var base = buildUpdateBase();
    var snap = captionSnapshot();
    return enqueue(function () {
      return resolveCaption(snap).then(function (caption) {
        var body = Object.assign({}, base, {
          text: caption.text,
          musicMentions: caption.mentions,
        });
        var key = JSON.stringify(body);
        if (skipIfUnchanged && key === lastSavedKeyRef.current) return null;
        lastSavedKeyRef.current = key;
        return api.patch('/api/lists/' + listId, body).catch(function (err: any) {
          // What the server holds is now unknown, so the next autosave sends.
          if (lastSavedKeyRef.current === key) lastSavedKeyRef.current = '';
          throw err;
        });
      });
    });
  }

  // Records what the server already holds, computed exactly the way
  // queueSave builds a body, so opening a list does not save it again.
  function seedBaseline() {
    var base = buildUpdateBase();
    var snap = captionSnapshot();
    lastSavedKeyRef.current = 'seeding';
    enqueue(function () {
      return resolveCaption(snap).then(function (caption) {
        if (lastSavedKeyRef.current !== 'seeding') return;
        lastSavedKeyRef.current = JSON.stringify(
          Object.assign({}, base, { text: caption.text, musicMentions: caption.mentions })
        );
      });
    });
  }

  // Autosave when editing an existing list (debounced)
  var autosaveTimerRef = useRef<any>(null);
  // The body of the last write sent, so an autosave that would send the same
  // thing is skipped. Writes are serialized, so this is checked when each one
  // runs: undoing an edit while its save is in flight still sends the undo.
  // Opening the editor alone re-sets `items` (the grid padding effect), and
  // without this check that saved the list with no changes: a published one
  // got marked "(edited)" just for being looked at, and a draft had every item
  // row rewritten.
  var lastSavedKeyRef = useRef<string | null>(null);

  function cancelAutosave() {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }

  function runAutosave() {
    return queueSave(true)
      .then(function () {
        setAutosaveError('');
      })
      .catch(function (err: any) {
        setAutosaveError(err?.response?.data?.reason || 'Failed to save. Please try again.');
      });
  }

  useEffect(
    function () {
      // Any list that exists on the server autosaves, including one created
      // here by a Publish that then failed. Gating on editingExisting left
      // that case saving nowhere: the local draft stops once the list exists.
      if (!savedListId) return;
      if (lastSavedKeyRef.current === null) {
        // First run: this is what the server already has.
        seedBaseline();
        return;
      }
      cancelAutosave();
      // Publishing ends on the success screen, and an autosave fired after the
      // publish lands would send items to a list that is now frozen.
      if (publishing || published) return;
      autosaveTimerRef.current = setTimeout(function () {
        autosaveTimerRef.current = null;
        runAutosave();
      }, 1500);
      // Deliberately no cleanup that cancels the timer: it also ran when the
      // editor closed, so an edit made in the last 1.5 seconds before tapping
      // Back was dropped. The effect below sends it instead.
    },
    // queueSave reads the same state listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      title,
      kind,
      orderStyle,
      items,
      text,
      musicMentions,
      prompt,
      editingExisting,
      savedListId,
      publishing,
      published,
    ]
  );

  // Always this render's runAutosave, so a flush from the unmount or page
  // handlers saves the newest state rather than the first render's.
  var runAutosaveRef = useRef(runAutosave);
  runAutosaveRef.current = runAutosave;
  // Set the moment a publish lands, so nothing the success screen triggers can
  // still write: the list is frozen and any items PATCH would be refused.
  var publishedRef = useRef(false);

  useEffect(function () {
    function flush() {
      if (!autosaveTimerRef.current || publishedRef.current) return;
      cancelAutosave();
      runAutosaveRef.current();
    }
    // Backgrounding a tab or switching apps on a phone may be the last chance
    // to run anything before the page is frozen or discarded.
    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') flush();
    }
    // Closing or reloading the tab cannot wait for a request, so send what is
    // pending and have the browser ask before leaving while a save is out.
    function onBeforeUnload(e: BeforeUnloadEvent) {
      flush();
      if (inFlightRef.current > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);
    return function () {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Leaving the editor inside the app: send the pending edit now.
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildCreateRequest() {
    var base = {
      user: { id: authTokens!.user.id },
      title: title.trim(),
      kind: kind,
      orderStyle: orderStyle,
      items: buildSubmitItems(),
      promptId: prompt ? prompt.id : undefined,
    };
    return resolveCaption(captionSnapshot()).then(function (caption) {
      var tags: string[] = [];
      var tagMatches = caption.text ? caption.text.match(/#([a-zA-Z0-9_]+)/g) : null;
      if (tagMatches) {
        tags = tagMatches.map(function (t: string) {
          return t.slice(1);
        });
      }
      return Object.assign({}, base, {
        text: caption.text || undefined,
        tags: tags.length > 0 ? tags : undefined,
        musicMentions: caption.mentions.length > 0 ? caption.mentions : undefined,
      });
    });
  }

  function handleSave() {
    if (!authTokens || !title.trim()) return;
    setSaving(true);
    setError('');

    if (savedListId) {
      // Update existing list. This write supersedes any pending autosave.
      cancelAutosave();
      queueSave(false)
        .then(function () {
          clearDraft();
          setSaving(false);
          setAutosaveError('');
          navigate('/post/' + savedListId);
        })
        .catch(function (err: any) {
          setError(err?.response?.data?.reason || 'Failed to save. Please try again.');
          setSaving(false);
        });
    } else {
      buildCreateRequest()
        .then(function (request) {
          return api.post('/api/lists', request);
        })
        .then(function (res: any) {
          clearDraft();
          setSaving(false);
          var id = res.data?.id;
          if (id) {
            // See the matching comment in handlePublish.
            lastSavedKeyRef.current = '';
            setSavedListId(id);
            navigate('/post/' + id);
          }
        })
        .catch(function (err: any) {
          setError(err?.response?.data?.reason || 'Failed to save. Please try again.');
          setSaving(false);
        });
    }
  }

  function handlePublish() {
    if (!canPublish || !authTokens) return;
    setPublishing(true);
    setError('');

    // Queued behind any write still in flight, so the status change cannot
    // overtake the save it depends on.
    function publishAfterSave(listId: string) {
      enqueue(function () {
        return api.patch('/api/lists/' + listId + '/status', { status: 'published' });
      })
        .then(function () {
          publishedRef.current = true;
          cancelAutosave();
          clearDraft();
          setPublishing(false);
          setPublished(true);
        })
        .catch(function (err: any) {
          setError(err?.response?.data?.reason || 'Failed to publish. Please try again.');
          setPublishing(false);
        });
    }

    if (savedListId) {
      // Save then publish. Cancelling the pending autosave matters here: if it
      // fired after the publish it would send items to a now-frozen list.
      cancelAutosave();
      queueSave(false)
        .then(function () {
          publishAfterSave(savedListId!);
        })
        .catch(function (err: any) {
          setError(err?.response?.data?.reason || 'Failed to save. Please try again.');
          setPublishing(false);
        });
    } else {
      // Create then publish
      buildCreateRequest()
        .then(function (request) {
          return api.post('/api/lists', request);
        })
        .then(function (res: any) {
          var id = res.data?.id;
          if (id) {
            // The list exists now even if publishing it fails.
            clearDraft();
            // The server holds what was sent, not necessarily what is on
            // screen now: an edit made while the create was in flight would
            // otherwise become the baseline and never be sent. An empty key
            // matches nothing, so the next autosave sends the current state.
            lastSavedKeyRef.current = '';
            setSavedListId(id);
            publishAfterSave(id);
          }
        })
        .catch(function (err: any) {
          setError(err?.response?.data?.reason || 'Failed to create list. Please try again.');
          setPublishing(false);
        });
    }
  }

  // Back to a draft so the items, their order and the style can change again.
  // A pending title or caption edit is saved first, while the list is still
  // published and so still accepts it.
  function handleUnpublish() {
    if (!savedListId || unpublishing) return;
    setUnpublishing(true);
    setError('');
    if (autosaveTimerRef.current) {
      cancelAutosave();
      runAutosave();
    }
    var listId = savedListId;
    enqueue(function () {
      return api.patch('/api/lists/' + listId + '/status', { status: 'unpublished' });
    })
      .then(function () {
        setListStatus('unpublished');
        setUnpublishing(false);
      })
      .catch(function (err: any) {
        setError(err?.response?.data?.reason || 'Failed to save. Please try again.');
        setUnpublishing(false);
      });
  }

  if (published) {
    return (
      <Page>
        <SuccessWrap>
          <Checkmark />
          <SuccessText>List published!</SuccessText>
          <FeedLink to="/">Return to feed</FeedLink>
        </SuccessWrap>
      </Page>
    );
  }

  return (
    <Page>
      <TopBar>
        <BackButton
          onClick={function () {
            navigate(-1);
          }}
        >
          &larr; Back
        </BackButton>
        <div style={{ display: 'flex', gap: '0.5em' }}>
          <BackButton onClick={handleSave} style={{ color: '#0066cc' }}>
            {saving ? 'Saving...' : 'Save'}
          </BackButton>
          {!isPublished && (
            <PublishButton
              $ready={canPublish}
              onClick={handlePublish}
              disabled={!canPublish}
              title={publishBlocker || undefined}
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </PublishButton>
          )}
        </div>
      </TopBar>

      {draftRestored && (
        <DraftBanner>
          <span>Draft restored</span>
          <DiscardBtn
            onClick={function () {
              clearDraft();
              setTitle('');
              setKind('songs');
              // Starts over in the style the + menu picked. A 3x3 needs its
              // nine empty squares set here: the padding effect runs only
              // when the style changes, and it may already be a 3x3.
              var fresh: 'playlist' | 'ranking' | 'grid' =
                !editingExisting &&
                (styleParam === 'playlist' || styleParam === 'ranking' || styleParam === 'grid')
                  ? styleParam
                  : 'playlist';
              setGridInsertIndex(null);
              setOrderStyle(fresh);
              setItems(fresh === 'grid' ? padToNine([]) : []);
              setText('');
              setMusicMentions([]);
              setDraftRestored(false);
            }}
          >
            Discard
          </DiscardBtn>
        </DraftBanner>
      )}

      {prompt && (
        <PromptResponseBanner
          text={prompt.text}
          onRemove={
            isPublished
              ? undefined
              : function () {
                  setPrompt(null);
                }
          }
        />
      )}

      {!editingExisting && !introDismissed && (
        <IntroCard>
          <IntroHeader>
            <IntroTitle>New! Create a List.</IntroTitle>
            <IntroDismiss onClick={dismissIntro} aria-label="Dismiss">
              &times;
            </IntroDismiss>
          </IntroHeader>
          <IntroBody>
            Put a few songs or albums together and share them as one post. A best of the year, a
            countdown, the soundtrack to your summer vacation, etc.
          </IntroBody>
          <IntroPoints>
            <li>Pick songs or albums, then add them in any order you like.</li>
            <li>Drag to reorder, and add a note to any item to say why it is there.</li>
            <li>It saves as a draft as you go. Nobody sees it until you publish.</li>
          </IntroPoints>
        </IntroCard>
      )}

      <SectionLabel>Title</SectionLabel>
      <TitleInput
        value={title}
        onChange={function (e) {
          setTitle(e.target.value);
        }}
        placeholder="My top albums of the year..."
        maxLength={120}
        required
      />

      <div style={{ display: 'flex', gap: '1em', marginTop: '0.75em' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SectionLabel>Kind</SectionLabel>
          <OptionSelect
            options={KIND_OPTIONS}
            value={kind}
            disabled={kindLocked || isPublished}
            onChange={function (v) {
              setKind(v as 'songs' | 'albums');
            }}
          />
          <OptionHint>
            {isPublished
              ? 'Locked after publish.'
              : kindLocked
                ? 'Locked once the list has items.'
                : KIND_OPTIONS.filter(function (o) {
                    return o.value === kind;
                  })[0]?.desc}
          </OptionHint>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SectionLabel>Style</SectionLabel>
          <OptionSelect
            options={styleOptions}
            value={orderStyle}
            disabled={isPublished}
            onChange={function (v) {
              // A square chosen in a 3x3 means nothing in another style.
              setGridInsertIndex(null);
              setOrderStyle(v as 'playlist' | 'ranking' | 'grid');
            }}
          />
          <OptionHint>
            {isPublished
              ? 'Locked after publish. Changing it would reorder the list under everyone who has already seen it.'
              : STYLE_OPTIONS.filter(function (o) {
                  return o.value === orderStyle;
                })[0]?.desc}
          </OptionHint>
        </div>
      </div>

      {isGrid && (
        <div style={{ marginBottom: '0.75em' }}>
          <SectionLabel style={{ marginTop: '0.75em' }}>Preview</SectionLabel>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '3px',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #ddd',
              borderRightWidth: '2px',
              borderBottomWidth: '2px',
              background: '#eee',
            }}
          >
            {Array.from({ length: 9 }, function (_, i) {
              var item = items[i];
              var artwork = item?.media?.artwork_url;
              var empty = !item || item.isEmpty;
              var fillable = empty && !isPublished;
              var targeted = fillable && searchOpen && gridInsertIndex === i;
              return (
                <div
                  key={i}
                  role={fillable ? 'button' : undefined}
                  tabIndex={fillable ? 0 : undefined}
                  aria-label={fillable ? 'Add to spot ' + (i + 1) : undefined}
                  onClick={
                    fillable
                      ? function () {
                          fillSlot(i);
                        }
                      : undefined
                  }
                  onKeyDown={
                    fillable
                      ? function (e) {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            fillSlot(i);
                          }
                        }
                      : undefined
                  }
                  style={{
                    cursor: fillable ? 'pointer' : undefined,
                    outline: targeted ? '2px solid black' : undefined,
                    outlineOffset: targeted ? '-2px' : undefined,
                    aspectRatio: '1',
                    overflow: 'hidden',
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {artwork ? (
                    <img
                      src={artwork
                        .replace('{w}x{h}', '100x100')
                        .replace('{w}', '100')
                        .replace('{h}', '100')}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <span style={{ color: '#999', fontSize: '1.4rem', fontWeight: 300 }}>+</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <SectionLabel>
        Items ({stripEmpty(items).length}
        {isGrid ? ' of 9' : ' of 100'})
      </SectionLabel>
      {isPublished && (
        <LockedNotice>
          <OptionHint style={{ marginTop: 0 }}>
            Locked after publish. Unpublish to change the items, their order or the style. It
            disappears from feeds until you publish it again.
          </OptionHint>
          <UnpublishButton onClick={handleUnpublish} disabled={unpublishing}>
            {unpublishing ? 'Unpublishing...' : 'Unpublish'}
          </UnpublishButton>
        </LockedNotice>
      )}
      <ItemsSection>
        {stripEmpty(items).length === 0 && !isGrid && (
          <EmptyState>Add 2 or more {kind} to get started.</EmptyState>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={items.map(function (it) {
              return it.id;
            })}
            strategy={verticalListSortingStrategy}
          >
            {items.map(function (item, i) {
              return (
                <SortableItem
                  key={item.id}
                  item={item}
                  index={i}
                  isRanking={isRanking}
                  isGrid={isGrid}
                  totalItems={stripEmpty(items).length}
                  locked={isPublished}
                  playState={playStateOf(item)}
                  onPlay={function (e) {
                    playItem(item, e);
                  }}
                  onNoteChange={function (val) {
                    handleNoteChange(item.id, val);
                  }}
                  onRemove={function () {
                    handleRemoveItem(item.id);
                  }}
                  onFillSlot={function () {
                    fillSlot(i);
                  }}
                  isTarget={isGrid && searchOpen && gridInsertIndex === i}
                />
              );
            })}
          </SortableContext>
        </DndContext>
        {dupeWarning && <DupeWarning>{dupeWarning}</DupeWarning>}
        {!isPublished && !isGrid && items.length < maxItems && !searchOpen && (
          <AddButton
            onClick={function () {
              setGridInsertIndex(null);
              setSearchOpen(true);
            }}
          >
            + Add {kind === 'songs' ? 'song' : 'album'}
          </AddButton>
        )}
        {searchOpen && !isPublished && (
          <div style={{ marginTop: '0.4em' }} ref={searchAreaRef}>
            <Search
              key={searchKey + '-' + kind}
              setMediaItem={handleAddMedia}
              autoFocus
              defaultType={kind as any}
            />
          </div>
        )}
      </ItemsSection>

      <SectionLabel>Say something (optional)</SectionLabel>
      <MentionInput
        value={text}
        onChange={function (e: any) {
          setText(e.target.value);
        }}
        musicMentions={musicMentions}
        onMusicMentionsChange={setMusicMentions}
        getSubmitTextRef={getSubmitTextRef}
        minRows={3}
        placeholder="What makes this list special?"
        style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.85rem' }}
      />

      {error && <ErrorText>{error}</ErrorText>}
      {autosaveError && <ErrorText role="alert">{autosaveError}</ErrorText>}

      <div style={{ display: 'flex', gap: '0.5em', marginTop: '1.25em' }}>
        <BackButton
          onClick={handleSave}
          style={{
            flex: 1,
            color: '#0066cc',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '0.6em',
            background: 'white',
          }}
        >
          {saving ? 'Saving...' : isPublished ? 'Save' : 'Save as Draft'}
        </BackButton>
        {!isPublished && (
          <PublishButton
            $ready={canPublish}
            onClick={handlePublish}
            disabled={!canPublish}
            title={publishBlocker || undefined}
            style={{ flex: 1, padding: '0.6em' }}
          >
            {publishing ? 'Publishing...' : 'Publish'}
          </PublishButton>
        )}
      </div>

      {!isPublished && publishBlocker && <PublishHint role="status">{publishBlocker}</PublishHint>}

      {playMenu && (
        <PlayQueueMenu
          pos={playMenu}
          onClose={function () {
            setPlayMenu(null);
          }}
          onPlayNow={function () {
            if (playMenuItem) playback.playNow([draftEntry(playMenuItem)]);
          }}
          onAddToQueue={function () {
            if (playMenuItem) playback.addToQueue([draftEntry(playMenuItem)]);
          }}
        />
      )}
    </Page>
  );
}
